/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { IConnectedCamera } from '../../../app-data/interfaces/connected-camera.interface';
import { Easing } from '@appCanvasCommon/tween/Easing';
import { Point, Rectangle } from 'pixi.js-legacy';
import { Ticket } from '@touchplan/common';
import { FocusOnTicketsOptions, ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { EditTicketsCombined } from '../../../app-data/models/EditTicketsCombined';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
/**
 * CameraService
 * The CameraService is intended to act as a bridge between the Angular and Canvas worlds.
 * When writing code, write it against the CameraSerivce API rather than the Camera API.
 * It is implemented using the facade pattern, with the actual logic being handled by the `Camera.ts` class.
 * The facade pattern was chosen for this to mask potentially complex behaviour in the Camera class, and to help mitigate the following problems:
 * 1. Avoid Tight Coupling: Writing directly against the Camera means Angular-land would need to understand intricate details on how the Camera works
 * 2. Avoid Complex Interface: The Camera could expose many methods and properties which are not relevant for most common use cases
 * 3. Inconsistent Usage: We want to encourage a consistent API to write against and avoid utilizing multiple different APIs to access the same functionality.
 */
@Injectable({ providedIn: 'root' })
export class CameraService extends AbstractCanvasConnection<ViewportChangedEvent> {
	/**
	 * Internal reference to the Canvas camera instance.
	 * This is set when registerPlanData is called after canvas initialization.
	 * @private
	 */
	private _camera: IConnectedCamera;
	/**
	 * Provides safe access to the camera instance.
	 * Throws an error if accessed before initialization
	 */
	get camera(): IConnectedCamera {
		if (!this._camera) {
			throw new Error(
				`CameraService: Tried to access the canvas camera before it was initialized. The canvas camera is initialized after the StateEvents.CANVAS_LOADED event is dispatched.`
			);
		}

		return this._camera;
	}
	/**
	 * Returns the current screen viewport rectangle.
	 * This represents the visible area in screen coordinates.
	 * Useful for determining what's currently visible to the user.
	 */
	get screen(): Rectangle {
		return this.camera.view;
	}
	/**
	 * Returns the current world viewport rectangle.
	 * This represents the visible area in world coordinates.
	 * Useful for calculations involving the actual position of objects in the world.
	 */
	get world(): Rectangle {
		return this.camera.globalView;
	}
	/**
	 * Returns the current world viewport scale.
	 */
	get scale(): Point {
		return this.camera.scale;
	}
	/**
	 * Smoothly moves the camera to a specific position using tweening.
	 * @param x - Target x coordinate in world space
	 * @param y - Target y coordinate in world space
	 * @param duration - Optional duration of the movement in milliseconds
	 * @param easing - Optional easing function to control the movement animation
	 */
	public moveTo(x: number, y: number, duration?: number, easing?: Easing): void {
		this.camera.tweenView(x, y, duration, easing);
	}
	/**
	 * Smoothly zooms the camera to a specific scale using tweening.
	 * @param z - Target zoom level
	 * @param duration - Optional duration of the zoom in milliseconds
	 * @param easing - Optional easing function to control the zoom animation
	 */
	public zoomTo(z: number, duration?: number, easing?: Easing): void {
		this.camera.tweenScale(z, duration, easing);
	}
	/**
	 * Combines movement and zoom into a single smooth transition.
	 * This is more efficient than calling moveTo and zoomTo separately
	 * as it synchronizes both animations.
	 * @param x - Target x coordinate in world space
	 * @param y - Target y coordinate in world space
	 * @param z - Target zoom level
	 * @param duration - Optional duration of the transition in milliseconds
	 * @param easing - Optional easing function to control the animation
	 */
	public moveToWithZoom(x: number, y: number, z: number, duration?: number, easing?: Easing): void {
		this.camera.tweenView(x, y, duration, easing);
		this.camera.tweenScale(z, duration, easing);
	}
	/**
	 * Immediately sets the camera position without animation.
	 * Optionally can also set the zoom level in the same operation.
	 * @param x - New x coordinate in world space
	 * @param y - New y coordinate in world space
	 * @param z - Optional new zoom level
	 */
	public setPosition(x: number, y: number, z?: number): void {
		if (z !== undefined) {
			this.camera.setPositionAndScale(x, y, z, z);
			return;
		}

		this.camera.setPosition(x, y);
	}
	/**
	 * Immediately sets the camera zoom level without animation.
	 * @param z - New zoom level
	 */
	public setScale(z: number): void {
		this.camera.setScale(z, z);
	}
	/**
	 * Converts a screen X coordinate to world X coordinate.
	 * Used when translating user input (like mouse clicks) to world positions.
	 * @param screenX - X coordinate in screen space
	 * @returns The corresponding X coordinate in world space
	 */
	public convertScreenToWorldX(screenX: number): number {
		return this.camera.screenToRealX(screenX);
	}
	/**
	 * Converts a screen Y coordinate to world Y coordinate.
	 * Used when translating user input (like mouse clicks) to world positions.
	 * @param screenY - Y coordinate in screen space
	 * @returns The corresponding Y coordinate in world space
	 */
	public convertScreenToWorldY(screenY: number): number {
		return this.camera.screenToRealY(screenY);
	}
	/**
	 * Converts a world X coordinate to screen X coordinate.
	 * Used when positioning UI elements over world objects.
	 * @param worldX - X coordinate in world space
	 * @returns The corresponding X coordinate in screen space
	 */
	public convertWorldXToScreen(worldX: number): number {
		return this.camera.realToScreenX(worldX);
	}
	/**
	 * Converts a world Y coordinate to screen Y coordinate.
	 * Used when positioning UI elements over world objects.
	 * @param worldY - Y coordinate in world space
	 * @returns The corresponding Y coordinate in screen space
	 */
	public convertWorldYToScreen(worldY: number): number {
		return this.camera.realToScreenY(worldY);
	}

	/**
	 * Scales the X coordinate from world space to screen space.
	 * @param x The X coordinate in world space
	 * @returns The X coordinate in screen space
	 */
	public scaleToScreenX(x: number): number {
		return this.camera.scaleToScreenX(x);
	}

	/**
	 * Scales the Y coordinate from world space to screen space.
	 * @param y The Y coordinate in world space
	 * @returns The Y coordinate in screen space
	 */
	public scaleToScreenY(y: number): number {
		return this.camera.scaleToScreenY(y);
	}

	/**
	 * Scales the X coordinate from screen space to world space.
	 * @param x The X coordinate in screen space
	 * @returns The X coordinate in world space
	 */
	public scaleToWorldX(x: number): number {
		return this.camera.scaleToWorldX(x);
	}

	/**
	 * Scales the Y coordinate from screen space to world space.
	 * @param y The Y coordinate in screen space
	 * @returns The Y coordinate in world space
	 */
	public scaleToWorldY(y: number): number {
		return this.camera.scaleToWorldY(y);
	}

	/**
	 * Initializes the camera service with the canvas camera instance.
	 * This method is called after the canvas is loaded and ready.
	 * Sets up the change subscription to notify observers of camera updates.
	 * @param canvasApplicationCamera - The initialized canvas camera instance
	 */
	public registerPlanData(canvasApplicationCamera: IConnectedCamera): void {
		super.registerPlanData();
		this._camera = canvasApplicationCamera;
		// Subscription is cleaned up when the CanvasViewComponent is destroyed
		this._subscription = canvasApplicationCamera.changed$.subscribe((event: ViewportChangedEvent) =>
			this._changed$.next(event)
		);
	}
	/**
	 * Centers the camera view on a specific point in the world space with a tween.
	 * This is particularly useful when you need to ensure certain elements are centered in the viewport with animation.
	 * @param x - The x coordinate in world space to center on
	 * @param y - The y coordinate in world space to center on
	 * @param z - Optional zoom level to apply while focusing. If provided, both position and zoom will be updated simultaneously
	 */
	public focusOn(x: number, y: number, z?: number): void {
		if (z !== undefined) {
			this.camera.focusOnXYZ(z, y, z);
			return;
		}

		this.camera.focusOnXY(x, y);
	}

	/**
	 * Centers the camera view on a specific ticket, taking into account the ticket's dimensions
	 * and any special focusing requirements defined in the options. This method is commonly used
	 * when navigating to a specific ticket, either through user interaction or programmatic control.
	 * @param ticket - The ticket object to focus on. The camera will center on the ticket's position
	 *                while considering its dimensions to ensure it's fully visible
	 * @param options - Optional configuration object that can modify the focusing behavior:
	 *                z: the final zoom level
	 *                align: left|center|right the position of the alignment relative to the camera
	 */
	public focusOnTicket(ticket: Ticket, options?: FocusOnTicketsOptions): void {
		this.camera.focusOnTicket(ticket, options);
	}

	/**
	 * Centers the camera view to encompass multiple tickets simultaneously. This method calculates
	 * the bounding box of all tickets in the provided map and adjusts the camera position and zoom
	 * to ensure all tickets are visible in the viewport.
	 * @param list - A Map containing ticket IDs and their corresponding Ticket objects. The camera
	 *              will adjust to show all tickets in this list within the viewport
	 */
	public focusOnTicketList(list: Map<string, Ticket>): void {
		this.camera.focusOnTickets(list);
	}

	/**
	 * Specialized focusing method designed specifically for the ticket editing workflow.
	 * This method handles the complex requirements of focusing during ticket editing,
	 * including handling multiple selected tickets, maintaining proper zoom levels for
	 * editing operations
	 * @param combination - Combined data structure containing information about tickets
	 *                     being edited, including their positions, dimensions, and edit state
	 * @param z - Optional explicit zoom level to use. If not provided, the zoom will be
	 *           automatically calculated to fit all edited tickets
	 * @param explicitCenterOffsetY - Optional vertical offset to apply to the camera position.
	 * @param centeringRect - Optional Rectangle that defines a specific area to center on
	 *
	 */
	public focusFromActionTicketEdit(
		combination: EditTicketsCombined,
		z?: number,
		explicitCenterOffsetY?: number,
		centeringRect?: Rectangle
	): void {
		this.camera.focusOnTicketsFromEdit(combination, z, explicitCenterOffsetY, centeringRect);
	}
	/**
	 * Intended to be a 1-to-1 replacement of the Hud.ts onTranslate method
	 * @param deltaX the screen space X position to translate the view matrix
	 * @param deltaY the screen space Y position to translate the view matrix
	 */
	public translate(deltaX: number, deltaY: number): void {
		this.camera.translate(deltaX, deltaY);
	}
	/**
	 * Intended to be a 1-to-1 replacement of the Hud.ts onTranslate method
	 * @param	point a Point object to transform the camera view matrix (in screen coordinates)
	 */
	public translateFromPoint(point: Point): void {
		this.camera.translateFromPoint(point);
	}
}
